/**
 * §14.6 degradation ladder (rungs 0–5, spine-locked) plus DECISIONS.md AMD-2 rungs 6–9,
 * and the AMD-2 device tiers.
 *
 * Every render subsystem reads this object; nothing else writes it. The §14.6 exempt set
 * — telegraphs, hazard fills/stripes, enemy outlines, elite pulse, spawn portals, status
 * cues, damage numbers, UI, target indicator — has no field here at all, which is how the
 * exemption is enforced: there is no knob to turn.
 */

export type PerfTier = 'desktop' | 'standard' | 'handheld';

export interface TierCaps {
  /** AMD-2 alive caps. */
  enemies: number;
  projectiles: number;
  particles: number;
  /** Internal buffer height ceiling, in physical pixels. */
  bufferHeight: number;
  /** Device pixel ratio ceiling before the ladder's own cap applies. */
  pixelRatio: number;
}

export const TIER_CAPS: Readonly<Record<PerfTier, TierCaps>> = {
  desktop: { enemies: 500, projectiles: 1500, particles: 3000, bufferHeight: 1080, pixelRatio: 2 },
  standard: { enemies: 260, projectiles: 800, particles: 1600, bufferHeight: 810, pixelRatio: 2 },
  handheld: { enemies: 140, projectiles: 450, particles: 900, bufferHeight: 540, pixelRatio: 1.5 },
};

export const MAX_RUNG = 9;

export interface QualityState {
  rung: number;
  tier: PerfTier;
  /** Alive caps from the tier (AMD-2). */
  enemyCap: number;
  projectileCap: number;
  particleCap: number;
  bufferHeight: number;
  /** Multiplier on the internal buffer size; rung 9 drops it to 0.8. */
  bufferScale: number;
  pixelRatioCap: number;
  /** Rungs 1–2: decorative emission rate multiplier. Gameplay-cued emitters ignore it. */
  decorativeRate: number;
  /** Rungs 2: explosion spark counts. */
  sparkScale: number;
  /** Rung 1: ambient mote density. */
  ambientScale: number;
  /** Rungs 3–4: ribbon length and width. See {@link BASE_TRAIL_SEGMENTS}. */
  trailSegments: number;
  trailWidth: number;
  /** Rungs 4–5: background parallax layers. */
  parallaxLayers: number;
  /** AMD-2 rungs 6–8. */
  persistence: boolean;
  halation: boolean;
  bloomLevels: number;
  /**
   * AMD-55 — the lomo corner blur, in output pixels. Four extra tube taps per pixel, so
   * it is the first thing the ladder spends: off from rung 6 (where persistence goes) and
   * off outright on the handheld tier, which is fill-rate bound before it is anything else.
   */
  edgeBlur: number;
}

/**
 * Baseline segment budget, which must equal `trails.MAX_SEGMENTS` — `TrailBuffer.track`
 * scales a ribbon's lifetime by `trailSegments / MAX_SEGMENTS`, so a mismatch shortens
 * every trail in the game at rung 0.
 *
 * AMD-39 raised it from the §11.6 24 alongside the ten-fold ribbon length. The §14.6
 * rungs below keep their *ratios* (rung 3 halves the ribbon, rung 4 takes it to a third)
 * rather than their absolute counts, so the longer baseline still degrades by the same
 * proportion instead of clipping to nothing.
 */
const BASE_TRAIL_SEGMENTS = 48;
const BASE_BLOOM_LEVELS = 6;
/** AMD-55 baseline corner softness, output pixels at the extreme corner. */
const BASE_EDGE_BLUR = 2.2;

export const quality: QualityState = {
  rung: 0,
  tier: 'desktop',
  enemyCap: TIER_CAPS.desktop.enemies,
  projectileCap: TIER_CAPS.desktop.projectiles,
  particleCap: TIER_CAPS.desktop.particles,
  bufferHeight: TIER_CAPS.desktop.bufferHeight,
  bufferScale: 1,
  pixelRatioCap: TIER_CAPS.desktop.pixelRatio,
  decorativeRate: 1,
  sparkScale: 1,
  ambientScale: 1,
  trailSegments: BASE_TRAIL_SEGMENTS,
  trailWidth: 1,
  parallaxLayers: 3,
  persistence: true,
  halation: true,
  bloomLevels: BASE_BLOOM_LEVELS,
  edgeBlur: BASE_EDGE_BLUR,
};

/** Bumped whenever the state changes, so subsystems can rebuild lazily. */
export let qualityGeneration = 0;

/**
 * Applies rung + tier. Rungs are cumulative: rung 4 also carries 1–3.
 * Nothing gameplay-visible is touched at any rung (spine §9 last line).
 */
export function applyQuality(rung: number, tier: PerfTier): void {
  const r = Math.max(0, Math.min(MAX_RUNG, Math.round(rung)));
  const caps = TIER_CAPS[tier];

  quality.rung = r;
  quality.tier = tier;
  quality.enemyCap = caps.enemies;
  quality.projectileCap = caps.projectiles;
  quality.bufferHeight = caps.bufferHeight;
  quality.pixelRatioCap = caps.pixelRatio;

  quality.decorativeRate = 1;
  quality.sparkScale = 1;
  quality.ambientScale = 1;
  quality.trailSegments = BASE_TRAIL_SEGMENTS;
  quality.trailWidth = 1;
  quality.parallaxLayers = 3;
  quality.particleCap = caps.particles;
  quality.persistence = true;
  quality.halation = true;
  quality.bloomLevels = BASE_BLOOM_LEVELS;
  quality.bufferScale = 1;
  quality.edgeBlur = tier === 'handheld' ? 0 : BASE_EDGE_BLUR;

  // §14.6 rungs 0–5, cumulative and in the spine-locked order.
  if (r >= 1) {
    quality.decorativeRate = 0.5;
    quality.ambientScale = 0.5;
  }
  if (r >= 2) {
    quality.decorativeRate = 0.25;
    quality.sparkScale = 0.5;
  }
  if (r >= 3) {
    quality.trailSegments = BASE_TRAIL_SEGMENTS / 2;
    quality.trailWidth = 0.75;
  }
  if (r >= 4) {
    quality.trailSegments = BASE_TRAIL_SEGMENTS / 3;
    quality.parallaxLayers = 1;
  }
  if (r >= 5) {
    quality.parallaxLayers = 0;
    quality.particleCap = Math.min(quality.particleCap, 1500);
  }
  // AMD-2 rungs 6–9.
  if (r >= 6) {
    quality.persistence = false;
    quality.edgeBlur = 0;
  }
  if (r >= 7) quality.halation = false;
  if (r >= 8) quality.bloomLevels = 4;
  if (r >= 9) {
    quality.bufferScale = 0.8;
    quality.pixelRatioCap = 1;
  }

  qualityGeneration++;
}

/** Boot probe: picks a tier from the device before any frame is drawn (AMD-2). */
export function probeTier(): PerfTier {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return 'desktop';
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const cores = typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : 4;
  const minSide = Math.min(window.innerWidth || 1280, window.innerHeight || 720);
  if (coarse && (cores <= 6 || minSide < 380)) return 'handheld';
  if (coarse) return 'standard';
  if (cores <= 4) return 'standard';
  return 'desktop';
}
